import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * ScrollNav — fixed floating buttons for quick "top" / "bottom" jumps.
 *
 * Integrates with Lenis if present on window (window.__lenis), otherwise
 * falls back to native window.scrollTo. The component fades in after the
 * user has scrolled enough that the buttons are useful.
 *
 * Hidden on small screens — the Sticky Mobile CTA Dock occupies that real
 * estate.
 */
export default function ScrollNav() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (y) => {
    if (window.__lenis && typeof window.__lenis.scrollTo === 'function') {
      window.__lenis.scrollTo(y, { duration: 1.2 });
    } else {
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const goTop = () => scrollTo(0);
  const goBottom = () => scrollTo(document.documentElement.scrollHeight);

  return (
    <div
      data-testid="scroll-nav"
      aria-hidden={!visible}
      className={`hidden sm:flex fixed right-5 lg:right-8 bottom-8 z-40 flex-col gap-2 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
    >
      <button
        type="button"
        onClick={goTop}
        data-testid="scroll-top-btn"
        aria-label="Scroll to top"
        className="w-11 h-11 rounded-full bg-white border border-black/10 shadow-[0_12px_28px_-12px_rgba(31,29,26,0.35)] inline-flex items-center justify-center text-[#1F1D1A] hover:bg-[#F26B1A] hover:text-white hover:border-[#F26B1A] transition-colors"
      >
        <ChevronUp className="w-5 h-5" strokeWidth={2.2} />
      </button>
      <button
        type="button"
        onClick={goBottom}
        data-testid="scroll-bottom-btn"
        aria-label="Scroll to bottom"
        className="w-11 h-11 rounded-full bg-white border border-black/10 shadow-[0_12px_28px_-12px_rgba(31,29,26,0.35)] inline-flex items-center justify-center text-[#1F1D1A] hover:bg-[#F26B1A] hover:text-white hover:border-[#F26B1A] transition-colors"
      >
        <ChevronDown className="w-5 h-5" strokeWidth={2.2} />
      </button>
    </div>
  );
}
